import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { OperatorImport } from "@/components/OperatorImport";
import { PageHero } from "@/components/PageHero";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg } from "@/lib/api";
import { CATEGORIES, categoryLabel, isStaff } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, inputCls } from "@/components/LegsEditor";

const OP_FIELDS = [["name", "Operator name", true], ["certificate", "Certificate number"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"]];
const TAIL_FIELDS = [["tail_number", "Tail number", true], ["aircraft_type", "Aircraft type", true], ["year", "Year of Make (YOM)", false, "number"],
  ["year_refurbished", "Year of Refurbishment (YOR)", false, "number"], ["seats", "Seats", false, "number"]];

function FormDialog({ title, fields, initial, onClose, onSubmit, withCategory, testId }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(f); onClose(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-lg" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-serif-display text-2xl font-normal text-white">{title}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          {fields.map(([k, label, req, type]) => (
            <Field key={k} label={label}>
              <Input className={inputCls} type={type || "text"} value={f[k] ?? ""} required={req} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${testId}-${k}-input`} />
            </Field>
          ))}
          {withCategory && (
            <Field label="Category">
              <Select value={f.aircraft_category || ""} onValueChange={(v) => setF({ ...f, aircraft_category: v })}>
                <SelectTrigger className={inputCls} data-testid={`${testId}-category-select`}><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="frame-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="frame-btn gold" disabled={busy} data-testid={`${testId}-save-btn`}>{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const num = (v) => (v === "" || v == null ? null : Number(v));

function TailRow({ op, t, onEdit, onChanged }) {
  const api = useApi();
  const del = () => api.delete(`/operators/${op.id}/tails/${t.tail_number}`).then(onChanged).catch((e) => toast.error(errMsg(e)));
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 border-t border-white/[0.06] text-sm" data-testid={`tail-${t.tail_number}`}>
      <span className="font-mono-data text-white w-24">{t.tail_number}</span>
      <span className="text-zinc-300 flex-1 min-w-[10rem]">{t.aircraft_type}{t.aircraft_category ? ` · ${categoryLabel(t.aircraft_category)}` : ""}</span>
      <span className="text-zinc-500 text-xs">{[t.year && `YOM ${t.year}`, t.year_refurbished && `YOR ${t.year_refurbished}`, t.seats && `${t.seats} seats`].filter(Boolean).join(" · ")}</span>
      <button className="text-zinc-400 hover:text-white" onClick={() => onEdit(t)} data-testid={`tail-${t.tail_number}-edit-btn`}><Pencil size={13} /></button>
      <button className="text-zinc-400 hover:text-rose-400" onClick={del} data-testid={`tail-${t.tail_number}-delete-btn`}><Trash2 size={13} /></button>
    </li>
  );
}

function OperatorCard({ op, onChanged, onEditOp }) {
  const api = useApi();
  const [tail, setTail] = useState(null);
  const saveTail = (f) => api.put(`/operators/${op.id}/tails`, { ...f, year: num(f.year), year_refurbished: num(f.year_refurbished), seats: num(f.seats) }).then(onChanged);
  const del = () => { if (window.confirm(`Remove ${op.name} and its tails from the list?`)) api.delete(`/operators/${op.id}`).then(onChanged).catch((e) => toast.error(errMsg(e))); };
  return (
    <article className="lux-card m-card p-5" data-testid={`operator-${op.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-2xl text-white">{op.name}</h3>
          <p className="text-xs text-zinc-500 mt-1">{[op.certificate && `Cert. ${op.certificate}`, op.phone, op.email, op.address].filter(Boolean).join(" · ") || "No contact details yet"}</p>
        </div>
        <div className="flex gap-2">
          <button className="frame-btn" onClick={() => setTail({})} data-testid={`operator-${op.id}-add-tail-btn`}><Plus size={13} /> Tail</button>
          <button className="frame-btn" onClick={() => onEditOp(op)} data-testid={`operator-${op.id}-edit-btn`}><Pencil size={13} /></button>
          <button className="frame-btn text-rose-400" onClick={del} data-testid={`operator-${op.id}-delete-btn`}><Trash2 size={13} /></button>
        </div>
      </div>
      <ul className="mt-4">{op.tails.map((t) => <TailRow key={t.tail_number} op={op} t={t} onEdit={setTail} onChanged={onChanged} />)}</ul>
      {!op.tails.length && <p className="text-xs text-zinc-500 mt-3">No tails yet.</p>}
      {tail && <FormDialog title={tail.tail_number ? `Edit ${tail.tail_number}` : `Add a tail to ${op.name}`} fields={TAIL_FIELDS} initial={tail} withCategory
        onClose={() => setTail(null)} onSubmit={saveTail} testId="tail-dialog" />}
    </article>
  );
}

export default function Operators() {
  const { me } = useMe();
  const api = useApi();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [importing, setImporting] = useState(false);
  const load = useCallback(() => api.get("/operators").then((r) => setList(r.data)).catch(() => setList([])), [api]);
  useEffect(() => { load(); }, [load]);
  if (!isStaff(me)) return <Navigate to="/" replace />;
  const term = q.trim().toLowerCase();
  const shown = (list || []).filter((o) => !term || o.name.toLowerCase().includes(term) || o.tails.some((t) => t.tail_number.toLowerCase().includes(term)));
  const saveOp = (f) => (edit.id ? api.patch(`/operators/${edit.id}`, f) : api.post("/operators", f)).then(load);
  return (
    <div data-testid="operators-page">
      <PageHero testId="operators-hero">
        <p className="eyebrow mb-3">Flight department</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white mb-3">Operators</h1>
        <p className="text-zinc-300 text-sm max-w-xl">Operators and their tails are suggested as you type on an option, and picking one fills in its details. New operators and tails entered on an option are added here automatically.</p>
      </PageHero>
      <section className="pb-10">
        <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-4xl">
          <div className="relative sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search operator or tail" className="pl-9 bg-[#0d0f14] border-white/10" data-testid="operators-search" />
          </div>
          <button className="frame-btn gold" onClick={() => setEdit({})} data-testid="operator-add-btn"><Plus size={13} /> Add operator</button>
          <button className="frame-btn" onClick={() => setImporting(true)} data-testid="operator-import-btn"><FileSpreadsheet size={13} /> Import Excel</button>
        </div>
        <div className="grid gap-4 max-w-4xl" data-testid="operators-list">
          {shown.map((op) => <OperatorCard key={op.id} op={op} onChanged={load} onEditOp={setEdit} />)}
          {list && !shown.length && <p className="text-sm text-zinc-500" data-testid="operators-empty">{list.length ? "No operators match your search." : "No operators yet. Add one, or they'll appear as options are added."}</p>}
        </div>
      </section>
      {importing && <OperatorImport onClose={() => setImporting(false)} onDone={load} />}
      {edit && <FormDialog title={edit.id ? `Edit ${edit.name}` : "Add an operator"} fields={OP_FIELDS} initial={edit} onClose={() => setEdit(null)} onSubmit={saveOp} testId="operator-dialog" />}
    </div>
  );
}
